// Configurable via VITE_* env vars (see .env.example) — same convention as
// VITE_API_URL in src/lib/apiClient.ts — so these can be tuned per
// environment (e.g. a slower cadence against a rate-limited dev tunnel)
// without a code change. Falls back to the given default whenever the env
// var is unset, empty, or not a positive number.
function readPollIntervalMs(raw: string | undefined, fallback: number): number {
  const parsed = Number(raw);
  return raw !== undefined && raw !== "" && Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

// How often UploadHistoryContext's pollUploadStatus checks a processing
// upload's coarse status/stage/progress.
export const UPLOAD_STATUS_POLL_MS = readPollIntervalMs(import.meta.env.VITE_UPLOAD_STATUS_POLL_MS, 3000);

// How often UploadDetailPanel re-fetches this upload's records while it's
// still "processing". 2500ms, not a faster round number: the backend
// converts pages in concurrent batches (GROQ_PARALLEL_PAGES, default 4) via
// an LLM call per page, and observed page-to-page completion gaps within a
// batch ranged ~0.2s-2.6s (mean ~1.3s) in live testing — a real page simply
// cannot commit new records faster than an LLM round-trip allows. Polling
// much faster than that (1000ms was tried and reliably tripped ngrok tunnel
// rate limiting/connection resets under combined load with the status poll)
// buys no earlier visibility into data that doesn't exist yet; 2500ms still
// samples every batch multiple times over a longer document's conversion
// window while roughly halving sustained request volume versus the previous
// 1500ms default.
//
// 5000ms since 2026-10-22: each records fetch is many database queries, and
// several open uploads polling at 2500ms helped fill the database's
// connections. The list's status poll still reports progress, and the panel
// fetches the full result the moment the upload finishes.
export const RECORDS_POLL_MS = readPollIntervalMs(import.meta.env.VITE_RECORDS_POLL_MS, 5000);

// Ceiling for the exponential backoff both polls apply after consecutive
// failures (see src/shared/lib/pollingBackoff.ts) — 30s caps how stale the
// view can get during a real outage while still giving a struggling
// connection real room to recover between attempts.
export const POLL_BACKOFF_MAX_MS = readPollIntervalMs(import.meta.env.VITE_POLL_BACKOFF_MAX_MS, 30_000);
